import { useMemo } from 'react';
import { Layout } from '../../components/Layout';
import Content from './Content';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectIsLoadingSubscriptionSummaries,
  selectActiveCluster,
  setActiveClusterId,
  setActiveSubscriptionId,
} from '../../store/slices/subscription';
import { useNavigate } from 'react-router-dom';
import { useGetSubscriptionSummariesByUserIdQuery } from '../../store/services/api';
import { useGetDemoSubscriptionSummariesQuery } from '../../store/services/demoApi';
import { selectUserId } from '../../store/slices/user';
import { selectIsDemo } from '../../store/slices/demo';
import { selectOverviewSubSort } from '../../store/slices/ui';
import useWidthBreakpoint from '../../hooks/useWidthBreakpoint';
import useDemoOutlierAutoDetect from '../../hooks/useDemoOutlierAutoDetect';
import { Transaction } from './types';
import type { SubscriptionSummary } from '../../types/subscription';
import { sortSubscriptions, SortOption } from './Content/FilterContext';
import { useDemoRebasedSubscriptions } from '../../store/hooks/useDemoRebasedSubscriptions';
import { useGetHideNearZeroEnabledQuery } from '../../store/services/api';
import { hiddenSummary, visibleSubscriptions } from './nearZero';

const STALE_PRICE_REPOLL_MS = 30_000;

/**
 * Derives every aggregate the Overview screen renders from the raw
 * subscription summaries. Pure `useMemo` chain — the demo-mode rebase lives
 * in `useDemoRebasedSubscriptions` so the Settings dropdown and the
 * Subscription detail page see exactly the same windowed values.
 */
const useOverviewClusterData = () => {
  const isLoading = useSelector(selectIsLoadingSubscriptionSummaries);
  const activeCluster = useSelector(selectActiveCluster);
  const subSort = useSelector(selectOverviewSubSort) as SortOption;
  const { visible: data } = useDemoRebasedSubscriptions();

  /**
   * `data` is the CALCULATION set and always stays complete — every total below derives from it,
   * retired subscriptions included. The near-zero switch only removes rows from what is
   * RENDERED, so an empty retired shell stops cluttering the grid without vanishing from the
   * all-time figures. See ./nearZero.
   */
  const userId = useSelector(selectUserId);
  const { data: hideNearZeroResponse } = useGetHideNearZeroEnabledQuery(
    userId,
    {
      skip: !userId,
    },
  );
  const hideNearZero = hideNearZeroResponse?.hideNearZeroEnabled ?? false;

  const activeClusterSubscriptions = useMemo<SubscriptionSummary[]>(() => {
    const inCluster = data.filter((s) =>
      activeCluster === null ? true : s.clusterId === activeCluster,
    );
    // Mirror the dashboard "Sort by" control so the sidebar list and the
    // main content grid stay in the same order.
    return sortSubscriptions(
      visibleSubscriptions(inCluster, hideNearZero),
      subSort,
    );
  }, [activeCluster, data, subSort, hideNearZero]);

  /**
   * The same cluster scope WITHOUT the near-zero filter. This is what the headline and the
   * portfolio chart are computed from: hiding is presentational, so it must never move
   * "since inception" or the chart's end value.
   */
  const activeClusterAllSubscriptions = useMemo<SubscriptionSummary[]>(() => {
    const inCluster = data.filter((s) =>
      activeCluster === null ? true : s.clusterId === activeCluster,
    );
    return sortSubscriptions(inCluster, subSort);
  }, [activeCluster, data, subSort]);

  /** What the switch removed from view, so the screen can account for the difference. */
  const hidden = useMemo(() => {
    const inCluster = data.filter((s) =>
      activeCluster === null ? true : s.clusterId === activeCluster,
    );
    return hiddenSummary(inCluster, hideNearZero);
  }, [activeCluster, data, hideNearZero]);

  const totalValueByCluster = useMemo(() => {
    if (!data.length) return {} as Record<string, number>;
    return data.reduce<Record<string, number>>((acc, sub) => {
      acc[sub.clusterId] = (acc[sub.clusterId] || 0) + sub.totalBalanceInStable;
      return acc;
    }, {});
  }, [data]);

  const costBasisByCluster = useMemo(() => {
    if (!data.length) return {} as Record<string, number>;
    return data.reduce<Record<string, number>>((acc, sub) => {
      acc[sub.clusterId] =
        (acc[sub.clusterId] || 0) + (sub.profits?.costBasis.stable || 0);
      return acc;
    }, {});
  }, [data]);

  const totalStableProfitByCluster = useMemo(() => {
    return Object.keys(totalValueByCluster).reduce<Record<string, number>>(
      (acc, clusterId) => {
        const cb = costBasisByCluster[clusterId];
        acc[clusterId] = cb ? (totalValueByCluster[clusterId] - cb) / cb : 0;
        return acc;
      },
      {},
    );
  }, [costBasisByCluster, totalValueByCluster]);

  const entirePortfolioValue = useMemo(
    () =>
      Object.values(totalValueByCluster).reduce((acc, curr) => acc + curr, 0),
    [totalValueByCluster],
  );

  const entirePortfolioProfit = useMemo(() => {
    const totalCostBasis = Object.values(costBasisByCluster).reduce(
      (acc, curr) => acc + curr,
      0,
    );
    if (totalCostBasis === 0) return 0;
    return (entirePortfolioValue - totalCostBasis) / totalCostBasis;
  }, [costBasisByCluster, entirePortfolioValue]);

  const activeClusterTotalValue = useMemo(() => {
    return activeCluster === null
      ? entirePortfolioValue
      : totalValueByCluster[activeCluster];
  }, [activeCluster, entirePortfolioValue, totalValueByCluster]);

  const hasPositiveProfit = useMemo<boolean | undefined>(() => {
    if (!data) return undefined;
    if (!activeCluster) {
      if (entirePortfolioProfit === 0) return undefined;
      return entirePortfolioProfit > 0;
    }
    return totalStableProfitByCluster[activeCluster] >= 0;
  }, [activeCluster, data, entirePortfolioProfit, totalStableProfitByCluster]);

  const transactions = useMemo(() => {
    // Each recent-transaction row is a subscription's latest transaction;
    // surface the subscription's authoritative current profit on it (the same
    // value the sidebar/leaderboard use) rather than the transaction's own
    // currentProfit, which the summary endpoint does not reliably populate.
    // `flatMap` with `[]` drops the null cases, so the result type is non-null.
    const items = activeClusterSubscriptions.flatMap((s) =>
      s.latestTransaction
        ? [
            {
              ...s.latestTransaction,
              currentProfit: s.currentProfit ?? null,
            },
          ]
        : [],
    );
    items.sort(
      (a, b) => (b.transactionTimestamp || 0) - (a.transactionTimestamp || 0),
    );
    return items as unknown as Transaction[];
  }, [activeClusterSubscriptions]);

  const hasData = !!Object.keys(totalValueByCluster).length;
  const hasMultipleClusters = Object.keys(totalValueByCluster).length > 1;

  return {
    activeCluster,
    activeClusterSubscriptions,
    activeClusterAllSubscriptions,
    totalValueByCluster,
    totalStableProfitByCluster,
    entirePortfolioValue,
    entirePortfolioProfit,
    activeClusterTotalValue,
    hasPositiveProfit,
    transactions,
    hidden,
    isLoading,
    hasData,
    hasMultipleClusters,
  };
};

const Overview: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector(selectUserId);
  const isDemo = useSelector(selectIsDemo);
  const { isMatch } = useWidthBreakpoint();

  // A row priced from a failed live batch self-heals: re-poll until every price is live again.
  const { data: summaryResponse } = useGetSubscriptionSummariesByUserIdQuery(
    userId,
    {
      refetchOnMountOrArgChange: true,
      skip: isDemo,
    },
  );
  const hasStalePrices = !!summaryResponse?.subscriptionSummary?.some(
    (s) => s.priceIsStale,
  );
  useGetSubscriptionSummariesByUserIdQuery(userId, {
    skip: isDemo || !hasStalePrices,
    pollingInterval: STALE_PRICE_REPOLL_MS,
  });
  useGetDemoSubscriptionSummariesQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: !isDemo,
  });

  // Demo-mode outlier auto-exclusion (>=50% of value) — encapsulated so this
  // screen doesn't carry the rules itself. No-op when not in demo mode.
  useDemoOutlierAutoDetect();

  const {
    activeCluster,
    activeClusterSubscriptions,
    activeClusterAllSubscriptions,
    totalValueByCluster,
    totalStableProfitByCluster,
    entirePortfolioValue,
    entirePortfolioProfit,
    activeClusterTotalValue,
    hasPositiveProfit,
    transactions,
    hidden,
    isLoading,
    hasData,
    hasMultipleClusters,
  } = useOverviewClusterData();

  // Card details (charts) load viewport-lazily: each card requests its own
  // details when it scrolls into view (see detailsLoader + BotItem), batched —
  // so we no longer fetch all 100+ cards' details up-front (that took minutes
  // and flooded candle-fetcher).

  const handleSubscriptionClick = (subscriptionId: number) => {
    dispatch(setActiveSubscriptionId(subscriptionId));
    navigate('sub/');
  };

  return (
    <Layout hasPositiveProfit={hasPositiveProfit}>
      {hasData ? (
        <Layout.SideMenu hasPositiveProfit={hasPositiveProfit}>
          <>
            <Layout.SideMenuSection>
              {isMatch('md') ? (
                <>
                  {hasMultipleClusters ? (
                    <>
                      <Layout.SideMenuTitle
                        title="Clusters"
                        count={Object.keys(totalValueByCluster).length}
                      />
                      <Layout.SideMenuItem
                        id={'Portfolio'}
                        label={'Portfolio'}
                        value={entirePortfolioValue}
                        delta={entirePortfolioProfit}
                        big
                        onClick={() => dispatch(setActiveClusterId(null))}
                        isActive={activeCluster === null}
                      />
                    </>
                  ) : (
                    <Layout.SideMenuTitle title="Portfolio" />
                  )}
                  {Object.entries(totalValueByCluster).map(
                    ([clusterId, value]) => (
                      <Layout.SideMenuItem
                        key={clusterId}
                        id={clusterId}
                        label={clusterId}
                        value={value}
                        delta={totalStableProfitByCluster[clusterId]}
                        big
                        onClick={(clusterId: string) =>
                          dispatch(setActiveClusterId(clusterId))
                        }
                        isActive={activeCluster === clusterId}
                      />
                    ),
                  )}
                </>
              ) : (
                <>
                  <Layout.SideMenuTitle title="Active Cluster" />
                </>
              )}
            </Layout.SideMenuSection>

            {isMatch('md') && activeClusterSubscriptions?.length ? (
              <Layout.SideMenuSection>
                <Layout.SideMenuTitle
                  title="Subscriptions"
                  count={activeClusterSubscriptions.length}
                  toggle
                />
                {activeClusterSubscriptions.map((sub) => (
                  <Layout.SideMenuItem
                    key={sub.id}
                    id={sub.id}
                    label={sub.botFriendlyName}
                    value={sub.totalBalanceInStable}
                    delta={sub.currentProfit}
                    deltaTotal={sub.profits?.total.stable.relative}
                    onClick={handleSubscriptionClick}
                  />
                ))}
              </Layout.SideMenuSection>
            ) : undefined}
          </>
        </Layout.SideMenu>
      ) : undefined}

      <Layout.Content noScroll>
        <Content
          subscriptions={activeClusterSubscriptions}
          allSubscriptions={activeClusterAllSubscriptions}
          totalValue={activeClusterTotalValue}
          transactions={transactions}
          hidden={hidden}
          isLoading={isLoading}
        />
      </Layout.Content>
    </Layout>
  );
};

export default Overview;
